<template>
  <modal
    :visible="$f.isAuthShown"
    allow-close
    @on-close="$f.$emit('login:close')"
  >
    <form
      class="auth-modal"
      @submit.prevent="submit"
    >
      <p class="title" v-text="title" />
      <p class="subtitle" v-text="subtitle" />
      <template v-if="mode !== 'register'">
        <ui-input
          v-show="mode === 'sms'"
          v-model="phone"
          type="tel"
          error=""
          label="Телефон*"
          placeholder="+7 (***) ***-**-**"
          mask="phone"
          validate="phone"
          clearable
        />
        <ui-input
          v-show="mode === 'email'"
          v-model="email"
          ref="email"
          type="text"
          validate="email"
          label="Email*"
          :error="emailError"
          @input="onInputEmail"
          clearable
        />
        <p class="error status" v-if="needConfirmEmail">
          Email не подтверждён. <span class="resend need-confirm-email" @click="handleSendConfirmEmail">Отправить письмо повторно</span>
        </p>

        <ui-input
          v-model="code"
          @input="codeError = ''"
          type="tel"
          name="code"
          mask="code"
          :loading="codeSent && progress"
          ref="codeInput"
          label="Код*"
          validate="code"
          :placeholder="codeSent ? 'Код подтверждения' : '••••••'"
          :disabled="!codeSent"
          :error="codeError"
        />

        <div class="status">
          <p v-if="retryTimeoutSeconds">
            Повторная отправка возможна через {{ retryTimeout }}
          </p>
          <p v-else-if="codeSent" @click="requestCode" class="resend">
            Отправить код повторно
          </p>
        </div>

        <ui-button
          :text="actionButton.text"
          :disabled="actionButton.disabled"
          :loading="progress"
          type="submit"
          wide
        />

        <ui-button flat wide
          :text="`Войти по ${mode === 'sms' ? 'Email' : 'номеру телефона'}`"
          @click.prevent="toggleMode"
        />
      </template>
      <!-- Регистрация -->
      <template v-else>
        <ui-input
          v-model="name"
          ref="name"
          error=""
          label="Имя*"
          placeholder="Имя"
          validate="name"
        />
        <ui-input
          v-model="email"
          ref="email"
          type="email"
          label="Email*"
          placeholder="Email"
          validate="email"
          :error="emailError"
          @input="onInputEmail"
        />
        <ui-checkbox
          v-model="subscription"
          label="Получать сообщения об акциях, скидках и новостях"
        />
        <ui-button
          ref="submit"
          :text="actionButton.text"
          :disabled="actionButton.disabled"
          :loading="progress"
          type="submit"
          wide
        />
      </template>

      <div class="agreement">
        <template v-if="mode === 'register'">
          Нажимая на кнопку «Зарегистрироваться» вы принимаете условия
          <a target="_blank" href="/agreement">пользовательского соглашения</a> и
          <a target="_blank" href="/fun-club">правила клубной программы</a>.
        </template>
        <recaptcha-text />
      </div>
    </form>
  </modal>
</template>

<script>
import {mapActions} from 'vuex';
import recaptchaText from '@/components/common/recaptchaText';
import validators from '../kit/ui-input/validators';
import { authGtmEvents } from '@/gtm/auth-gtm-events';

const SMS = 'sms';
const EMAIL = 'email';
const REGISTER = 'register';
const PHONE = 'phone';

export default {
  name: 'auth-modal',
  components: {
    recaptchaText,
  },
  metaInfo () {
    return {
      script: [
        {
          vmid: 'data-recaptcha',
          src: 'https://www.google.com/recaptcha/api.js?render=6Lft2EkbAAAAAN1EJWXJvqqBtqzAPCNO3GtTgFNK',
          async: true,
          defer: true,
          once: true,
          body: true
        }
      ]
    }
  },
  data: () => ({
    mode: SMS, // sms || email || register
    phone: '',
    email: '',
    code: '',
    codeIsValid: false,
    name: '',
    token: '',
    subscription: true,
    codeSent: false,
    retryTimeoutSeconds: 0,
    error: '',
    emailError: '',
    codeError: '',
    progress: false,
    needConfirmEmail: false,
  }),
  computed: {
    title() {
      return this.mode === REGISTER ? 'Зарегистрироваться' : 'Войти или зарегистрироваться';
    },
    subtitle() {
      switch (this.mode) {
        case 'sms':
          return this.codeSent
            ? 'Код подтверждения отправлен на указанный номер'
            : 'На указанный номер будет отправлен код подтверждения'
        case 'email':
          return `На указаный Email${!this.codeSent ? ' будет' : ''} отправлен код подтверждения`
        default: return '';
      }
    },
    phoneValid() {
      return this.phone.replace(/\D/g, '').length === 11;
    },
    codeValid() {
      return !validators.code.some(([regex]) => !(new RegExp(regex)).test(this.code));
    },
    isRegisterDataValid() {
      const isNameValid = this.name.length >= 2 && this.$refs.name?.valid;
      const isEmailValid = this.$refs.email?.valid && !this.emailError;
      return isNameValid && isEmailValid;
    },
    retryTimeout() {
      const minutes = Math.floor(this.retryTimeoutSeconds / 60);
      const seconds = this.retryTimeoutSeconds - minutes * 60;
      return `${minutes ? minutes + ' мин' : ''} ${seconds} с`;
    },
    actionButton() {
      switch (this.mode) {
        case SMS:
          return this.codeSent
            ? { text: 'Войти', disabled: !this.codeValid }
            : { text: 'Получить код', disabled: !this.phoneValid || this.retryTimeoutSeconds > 0 }
        case EMAIL:
          return this.codeSent
            ? { text: 'Войти', disabled: !this.codeValid }
            : { text: 'Получить код', disabled: !this.$refs.email?.valid || !!this.emailError }
        case REGISTER:
          return { text: 'Зарегистрироваться', disabled: !this.isRegisterDataValid }
        default:
          return { text: 'Войти', disabled: true }
      }
    },
  },

  watch: {
    phone() {
      this.reset({ skip: ['phone'] });
    },
    '$f.isAuthShown' (shown) {
      const { authData } = this.$f;

      if (!shown) this.resetInputsAndState();
      else if (authData) {
        this.phone = authData.phone || '';
        this.email = authData.email || '';

        if (authData.mode) {
          // в компоненте есть работа с нереактивными данными, переключаем мод для актуализации
          // todo: выкинуть из computed не реактивные данные (refs)
          this.$nextTick(() => this.mode = authData.mode);
        }
      }
    },
  },

  methods: {
    ...mapActions({
      LOGIN: 'user/login',
      REGISTER: 'user/register',
      sendConfirmEmail: 'user/sendConfirmEmail',
    }),
    submit() {
      if (this.actionButton.disabled) return;
      switch (this.mode) {
        case SMS:
          return this.codeSent
            ? this.verifyCode()
            : this.requestCode()
        case EMAIL:
          return this.codeSent
            ? this.verifyCode()
            : this.requestEmailCode()
        case REGISTER: return this.register();
      }
    },
    requestEmailCode() {
      this.checkExists()
        .then(this.requestCode)
        .catch((error) => {
          console.log(error)
          this.emailError = 'Введённый Email не существует в системе. Попробуйте авторизоваться по номеру телефона';
        })
    },
    requestCode() {
      this.progress = true;
      const request = {
        channel: this.mode,
      }
      if (this.mode === SMS) request.phone = this.phone;
      if (this.mode === EMAIL) request.email = this.email;

      this.$f.requestCode({ request })
        .then(r => {
          this.progress = false;

          if (r?.data?.needConfirmEmail) {
            this.needConfirmEmail = true;

            return;
          }

          this.codeSent = true;
          this.retryTimeoutSeconds = r.retryTimeoutSeconds;
          this.$refs.codeInput && this.$nextTick(() => {
            this.$refs.codeInput.clear();
            this.$refs.codeInput.focus();
          });
          this.retryCountdown();
        })
         .catch(({ error = {} } = {}) => {
          this.progress = false;
          this.codeSent = false;
          this.$store.dispatch('notifications/create', {
            title: 'Ошибка',
            message: error.detail || 'Не получилось отправить код подтверждения',
          });
        })
    },
    verifyCode() {
      this.progress = true;
      const request = {
        code: this.code,
      }
      if (this.mode === SMS) request.phone = this.phone;
      if (this.mode === EMAIL) request.email = this.email;

      this.$store.$api.verifyCode(request)
        .then(r => {
          this.token = r.token;
          this.mode === SMS ? this.login() : this.loginWithEmail();
        })
        .catch(({ error = {} } = {}) => {
          this.progress = false;
          if (error.code) { // пришел ответ от бека
            this.code = '';
            this.codeIsValid = false;
            this.codeError = error.detail;
          }
          else { // упал бек
            this.$store.dispatch('notifications/create', {
              title: 'Ошибка',
              message: 'Не получилось проверить код',
            });
          }
        })
    },
    checkExists() {
      return new Promise((resolve, reject) => {
        this.$store.$api.accountExists({
          type: this.mode === SMS ? PHONE : EMAIL,
          value: this.mode === SMS ? this.phone : this.email,
          scanUnconfirmed: true,
        })
          .then(r => r.exists ? resolve(r) : reject(r))
          .catch(reject);
      });
    },
    async checkEmailExists() {
      const { exists } = await this.$store.$api.accountExists({
        type: EMAIL,
        value: this.email,
        scanUnconfirmed: true,
      });
      return exists;
    },
    login() {
      this.checkExists()
        .then(() => {
          this.LOGIN({
            value: this.phone,
            token: this.token,
          })
          .then(this.onAuthSuccess)
          .finally(() => {
            this.progress = false;
          });
        })
        .catch(() => { // exists false
          this.progress = false;
          this.mode = REGISTER;
        });
    },
    loginWithEmail() {
      this.LOGIN({
        type: EMAIL,
        value: this.email,
        token: this.token,
      })
      .then(this.onAuthSuccess)
      .catch(({ error = {} } = {}) => {
        if (error.needConfirmEmail) {
          this.needConfirmEmail = true;

          return;
        }

        this.$store.dispatch('notifications/create', {
          title: 'Ошибка',
          message: error.title || '',
        });
      })
      .finally(() => {
        this.progress = false;
      });
    },
    retryCountdown() {
      this.retryTimeoutSeconds--;
      if (this.retryTimeoutSeconds <= 0) {
        this.retryTimeoutSeconds = 0;
        return;
      }
      setTimeout(this.retryCountdown, 1000);
    },
    async register() {
      this.progress = true;

      if (await this.checkEmailExists()) {
        this.emailError = 'Введённый Email существует в системе. Попробуйте авторизоваться по номеру телефона';
        this.progress = false;
        return;
      }

      return this.REGISTER({
        name: this.name,
        token: this.token,
        email: this.email,
        subscription: this.subscription,
        phone: this.phone,
      })
        .then((account) => {
          this.$f.$emit('login');
          authGtmEvents.successRegistration({
            mode: SMS,
            id: account?.id,
            name: account?.firstName,
            email: account?.email,
            mobilePhone: account?.phone
          });
        })
        .catch(({ error = {} } = {}) => {
          this.$store.dispatch('notifications/create', {
            title: 'Ошибка',
            message: error.title || '',
          });
        })
        .finally(() => {
          this.progress = false;
        });
    },
    toggleMode() {
      this.mode = this.mode === SMS ? EMAIL : SMS;
      this.reset();
    },
    onAuthSuccess(account) {
      this.$f.$emit('login');

      authGtmEvents.successAuth({ mode: this.mode, email: account?.email });
    },
    reset({ skip = [] } = {}) {
      Object.entries({
        codeSent: false,
        code: '',
        token: '',
        error: '',
        codeError: '',
        emailError: '',
        needConfirmEmail: false,
        progress: false,
      })
        .filter(([key]) => !skip.includes(key))
        .forEach(([key, value]) => this[key] = value)
      ;
    },
    resetInputsAndState() {
      this.phone = '';
      this.email = '';
      this.code = '';
      this.name = '';
      this.subscription = true;
      this.codeSent = false;

      this.reset();
      this.mode = SMS;
    },
    async handleSendConfirmEmail() {
      try {
        await this.sendConfirmEmail({ email: this.email });
        this.needConfirmEmail = false;
      } catch(e) {
        console.error(e)
      }
    },
    onInputEmail() {
      this.emailError = '';
      this.needConfirmEmail = false;
    },
  },
}
</script>

<style lang="scss">
.auth-modal {
  max-width: 100%;
  .ui-input {
    min-height: initial;
  }
  .title {
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    margin-bottom: 12px;
  }
  .subtitle {
    font-size: 12px;
    line-height: 20px;
    color: $gray;
  }
  .ui-button {
    margin-top: 12px;
  }
  .ui-input {
    margin-top: 12px;
  }
  .ui-checkbox {
    margin: 20px 0 24px;
  }
  .status {
    margin-top: 4px;
    min-height: 20px;
    font-size: 12px;
    line-height: 16px;
    color: $gray;
  }
  .resend {
    display: inline-block;
    cursor: pointer;
    color: $blue;
  }

  .agreement {
    margin-top: 24px;
    padding-top: 12px;
    border-top: 1px solid $gray_lu;
    font-size: 12px;
    line-height: 16px;
    color: $gray;

    a {
      color: $black;
    }
  }

  .error {
    color: $red;
  }
}

@include breakpoint("tablet") {
  .auth-modal {
    width: 392px;
  }
}
</style>
